import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { subscribeTriageItems } from '../../services/PortalInboxRepository';
import { toTitleCasePt } from '../../utils/formatters';

const toDateLabel = (v) => {
  try {
    if (v?.toDate) return v.toDate().toLocaleString();
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
};

const resolveClientName = (row) => {
  return String(row?.clientName || row?.client?.name || row?.client?.fullName || '').trim() || 'Cliente';
};

const resolveFileName = (row) => {
  return String(row?.fileName || row?.filename || row?.name || '').trim() || 'Arquivo';
};

const resolveFileUrl = (row) => {
  return String(row?.fileUrl || row?.url || row?.webViewLink || '').trim();
};

export default function PortalInbox({ onOpenUploadCenter = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const list = useMemo(() => {
    return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => {
      const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a?.createdAt || 0).getTime();
      const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b?.createdAt || 0).getTime();
      return (bv || 0) - (av || 0);
    });
  }, [rows]);

  const handleLegacyAction = () => {
    if (typeof onOpenUploadCenter === 'function') {
      setActionError('');
      onOpenUploadCenter();
      return;
    }
    setActionError('Esta tela agora funciona apenas como monitor da fila. Use a Central de Uploads para aprovar, devolver e arquivar com a estrutura Autos -> Peça -> Anexos.');
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    const unsub = subscribeTriageItems(
      (out) => {
        if (!mounted) return;
        setRows(out);
        setLoading(false);
      },
      (e) => {
        if (!mounted) return;
        setError(String(e?.message || e || 'Falha ao carregar a caixa de entrada'));
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      try { unsub && unsub(); } catch { void 0; }
    };
  }, []);

  return (
    <div className="h-full w-full flex flex-col bg-gray-50">

      {actionError ? (
        <div className="px-6 py-3 bg-red-50 border-b border-red-100 text-red-700 text-xs font-bold whitespace-pre-wrap">{actionError}</div>
      ) : null}

      <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 text-amber-900 text-xs font-bold whitespace-pre-wrap">
        Painel legado em modo somente leitura. Para manter a coerência entre GED, triagem e Google Drive estrutural, use a Central de Uploads para executar Aprovar e Rejeitar.
      </div>

      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar">
        {loading ? (
          <div className="p-8">
            <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-6">
              <div className="text-xs text-gray-600">Carregando caixa de entrada...</div>
            </div>
          </div>
        ) : list.length === 0 ? (
          <div className="p-8">
            <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-8">
              <div className="text-sm font-extrabold text-[#20243B]">Nenhuma pendência. A triagem está limpa.</div>
              <div className="mt-2 text-xs text-gray-600">Quando o cliente enviar um arquivo, ele aparecerá aqui automaticamente.</div>
            </div>
          </div>
        ) : (
          <div className="p-4 md:p-6">
            <div className="hidden md:block border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="grid grid-cols-12 gap-0 bg-gray-50 border-b border-gray-200 px-4 py-2 text-[10px] font-extrabold uppercase tracking-widest text-gray-500">
                <div className="col-span-3">Cliente</div>
                <div className="col-span-3">Arquivo</div>
                <div className="col-span-2">Enviado</div>
                <div className="col-span-2">Documento</div>
                <div className="col-span-2 text-right">Ações</div>
              </div>

              <div className="divide-y divide-gray-100">
                {list.map((row) => {
                  const clientName = toTitleCasePt(resolveClientName(row));
                  const fileName = resolveFileName(row);
                  const sentAt = toDateLabel(row?.createdAt);
                  const url = resolveFileUrl(row);

                  return (
                    <div key={row.id} className="grid grid-cols-12 gap-0 px-4 py-3 text-xs items-center">
                      <div className="col-span-3 pr-3">
                        <div className="font-bold text-slate-800 truncate" title={clientName}>
                          {clientName}
                        </div>
                      </div>
                      <div className="col-span-3 pr-3">
                        <div className="text-slate-700 truncate" title={fileName}>
                          {fileName}
                        </div>
                      </div>
                      <div className="col-span-2 pr-3 text-[11px] text-slate-500">{sentAt}</div>
                      <div className="col-span-2 pr-3">
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[#20243B] hover:bg-gray-50"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Visualizar Documento
                          </a>
                        ) : (
                          <span className="text-[11px] font-bold text-gray-400">Indisponível</span>
                        )}
                      </div>
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleLegacyAction}
                          className="rounded-md bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest"
                        >
                          Abrir na Central
                        </button>
                        <button
                          type="button"
                          onClick={handleLegacyAction}
                          className="rounded-md bg-slate-600 hover:bg-slate-700 text-white px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest"
                        >
                          Usar Fluxo Atual
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="md:hidden space-y-3">
              {list.map((row) => {
                const clientName = toTitleCasePt(resolveClientName(row));
                const fileName = resolveFileName(row);
                const sentAt = toDateLabel(row?.createdAt);
                const url = resolveFileUrl(row);

                return (
                  <div key={row.id} className="border border-gray-200 rounded-lg bg-white shadow-sm p-4">
                    <div className="text-xs font-extrabold text-[#20243B] truncate" title={clientName}>
                      {clientName}
                    </div>
                    <div className="mt-1 text-xs text-gray-600 truncate" title={fileName}>
                      {fileName}
                    </div>
                    <div className="mt-2 text-[11px] text-gray-500">Enviado: {sentAt}</div>

                    <div className="mt-3 flex flex-col gap-2">
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[#20243B]"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Visualizar Documento
                        </a>
                      ) : (
                        <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-gray-400 text-center">
                          Indisponível
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={handleLegacyAction}
                          className="rounded-md bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest"
                        >
                          Central
                        </button>
                        <button
                          type="button"
                          onClick={handleLegacyAction}
                          className="rounded-md bg-slate-600 hover:bg-slate-700 text-white px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest"
                        >
                          Fluxo Atual
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
